import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Share the seller JWT so Website Builder on :3050 can read it. */
export function writeWebsiteBuilderCookie(explicit?: string | null): void {
  if (typeof document === 'undefined') return;
  const token =
    explicit === undefined
      ? typeof localStorage === 'undefined'
        ? null
        : localStorage.getItem('token')
      : explicit;
  if (token) {
    document.cookie = `mb_seller_token=${encodeURIComponent(token)}; Path=/; SameSite=Lax; Max-Age=${60 * 60 * 24 * 7}`;
  } else {
    document.cookie = 'mb_seller_token=; Path=/; Max-Age=0';
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly USER_KEY = 'loggedInUser';
  user: any = {};
  apiUrl: string = environment.apiUrl;
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private isLoggedInSubject = new BehaviorSubject<boolean>(this.hasUser());
  isLoggedIn$ = this.isLoggedInSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private router: Router,
  ) {}

  login(payload: { email: string; password: string }, endPoint: string): Observable<any> {
    // Replace this with your real API endpoint
    return this.http.post<any>(`${this.apiUrl}/${endPoint}`, payload).pipe(
      tap((user) => {
        this.setUser(user);
        // Store token in localStorage if provided in response
        if (user && user.token) {
          if (this.isBrowser()) {
            localStorage.setItem('token', user.token);
            writeWebsiteBuilderCookie(user.token);
          }
        }
      }),
    );
  }

  setUser(user: any) {
    if (this.isBrowser()) {
      // Handle different response structures
      // API might return { user: {...}, token: ... } or just the user object
      let userToStore = user;
      
      // If user is nested in response (e.g., { user: {...}, token: ... })
      if (user && user.user && typeof user.user === 'object') {
        userToStore = user.user;
      }
      
      // Remove token from user object before storing (it's stored separately)
      const { token, ...userWithoutToken } = userToStore;
      localStorage.setItem(this.USER_KEY, JSON.stringify(userWithoutToken));
      this.isLoggedInSubject.next(true);
      writeWebsiteBuilderCookie();
      
      // Track login time and activity for session management
      sessionStorage.setItem('lastLoginTime', Date.now().toString());
      sessionStorage.setItem('lastActivityTime', Date.now().toString());
    }
  }

  /** True when seller (or child account) is approved to sell (business verification complete). */
  sellerCanOperate(): boolean {
    const u = this.getUser();
    if (!u) return false;
    return u.isActive === true;
  }

  /** Merge flags from API (e.g. profile) so UI updates after admin approval without re-login. */
  patchUser(partial: Record<string, unknown>): void {
    if (!this.isBrowser()) return;
    const cur = this.getUser() || {};
    const next = { ...cur, ...partial };
    localStorage.setItem(this.USER_KEY, JSON.stringify(next));
    this.isLoggedInSubject.next(true);
  }

  getUser(): any {
    if (this.isBrowser()) {
      try {
        const user = localStorage.getItem(this.USER_KEY);
        if (user) {
          const parsedUser = JSON.parse(user);
          // Update the BehaviorSubject if user exists
          if (parsedUser && !this.isLoggedInSubject.value) {
            this.isLoggedInSubject.next(true);
          }
          return parsedUser;
        }
      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
        // Clear corrupted data
        localStorage.removeItem(this.USER_KEY);
      }
    }
    return null;
  }

  signInWithGoogle(idToken: string) {
    return this.http.post(`${environment.apiUrl}/auth/google`, { idToken });
  }

  getToken() {
    if (this.isBrowser()) {
      return localStorage.getItem('token');
    }
    return null;
  }

  /**
   * Share the seller JWT on localhost (all ports) so Website Builder on :3050
   * can pick it up without a fresh login. Production still uses ?token= SSO.
   */
  syncWebsiteBuilderCookie(explicit?: string | null): void {
    if (!this.isBrowser()) return;
    writeWebsiteBuilderCookie(explicit);
  }

  logout(route: any) {
    if (this.isBrowser()) {
      localStorage.removeItem(this.USER_KEY);
      localStorage.removeItem('token');
      localStorage.removeItem('rememberedUser'); // Clear remembered user data
      writeWebsiteBuilderCookie(null);
    }
    this.isLoggedInSubject.next(false);
    this.http.post(`${environment.apiUrl}/auth/logout`, {});
    this.router.navigate([`/${route}`]);
  }

  private hasUser(): boolean {
    if (this.isBrowser()) {
      return !!localStorage.getItem(this.USER_KEY);
    }
    return false;
  }

  // Check if token is valid and refresh if needed
  checkTokenValidity(): Observable<boolean> {
    const token = this.getToken();
    if (!token) {
      this.isLoggedInSubject.next(false);
      return of(false);
    }

    // Verify token with backend
    return this.http.get<any>(`${this.apiUrl}/auth/checkAuth`).pipe(
      map((user) => {
        if (user && user.email) {
          // Token is valid, update user data
          this.setUser(user);
          this.isLoggedInSubject.next(true);
          return true;
        } else {
          // Token invalid, clear session
          this.logout('sellar-login');
          return false;
        }
      }),
      catchError((error) => {
        // Token expired or invalid
        if (this.isBrowser()) {
          localStorage.removeItem(this.USER_KEY);
          localStorage.removeItem('token');
          writeWebsiteBuilderCookie(null);
        }
        this.isLoggedInSubject.next(false);
        return of(false);
      })
    );
  }
}
